<template>
    <div class="">
        <div class="relative h-screen">
            <video
                v-if="content.data['top-video']"
                class="hero-image !fixed"
                :src="`/api/files/video/${content.data['top-video']}`"
                loop
                playsinline
                muted
                autoplay
                v-show="show_video"
                :poster="`/api/files/video/thumbnail/${content.data['top-video']}?full=true`"
            />
            <ResponsiveImage v-else-if="content.data['hero-image']" v-show="show_video" :image-id="content.data['hero-image']" class="hero-image !fixed"/>
            <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/25 to-transparent to-20% z-10"></div>
            <div
                class="relative z-20 flex flex-col items-start justify-end h-full text-white text-left container"
            >
                <h1
                    :lang="mainStore.active_locale === 'en_GB' ? 'en' : 'nl'"
                    class="font-bold view-h1 opacity-0 md:opacity-100 hyphens-auto col-span-10 md:col-span-7 self-end !col-start-1 pb-8"
                    ref="elementRef"
                >{{ get_hyphenated_content(content?.data.title[mainStore.active_locale])}}</h1>
            </div>
        </div>

        <div ref="videoTrackingElement" class="opacity-0 absolute top-0 left-0 h-screen w-full"></div>

        <div class="relative bg-background pt-12 pb-24 flex flex-col gap-24  items-center max-w-full overflow-hidden">
            <div class="container flex flex-col gap-y-16">
                <div class="flex flex-col gap-y-12 col-span-8 !col-start-1">
                    <div class="view-h1 opacity-100 md:opacity-0 md:hidden lg:!hidden">{{ content?.data.title[mainStore.active_locale] }}</div>
                    <div class="text-subtitle">{{ content?.data?.subtitle?.[mainStore.active_locale] }}</div>
                    <div class="text-intro">
                        <RenderedHTML :value="content?.data.intro" />
                    </div>
                </div>
                <div class="sm:min-w-4/12 max-w-full md:col-start-10 md:col-span-3">
                    <div class="bg-y-sand/20 rounded-sm p-4 text-sm flex flex-col gap-6 transition-all">
                        <div v-if="credits" v-for="credit in credits.slice(0, 4)" class="flex flex-col gap-0">
                            <div class="font-semibold">{{ t(`credit-label.${ credit?.key }`) }}</div>
                            <div>{{ credit?.value }}</div>
                        </div>
                        <div
                            v-if="credits.length > 4"
                            class="transition-all grid !grid-rows-[0fr] "
                            :class="{'!grid-rows-[1fr]': show_full_credit}"
                        >
                            <div class="overflow-hidden flex flex-col gap-6 !col-start-1">
                                <div

                                    v-for="credit in credits.slice(4)"
                                    class="flex-col gap-0"
                                >
                                    <div class="font-semibold">{{ t(`credit-label.${ credit?.key }`) }}</div>
                                    <div>{{ credit?.value }}</div>
                                </div>

                            </div>
                        </div>
                        <div class="min-w-48" v-if="credits.length > 4">
                            <div
                                @click="show_full_credit = true"
                                v-show="!show_full_credit"
                                class="underline cursor-pointer flex flex-row gap-2 items-center"
                            >
                                <CaretDownSlim class="w-2 h-auto"></CaretDownSlim>
                                <span>{{t('credit-label.button-open')}}</span>
                            </div>
                            <div
                                @click="show_full_credit = false"
                                v-show="show_full_credit"
                                class="underline cursor-pointer flex flex-row gap-2 items-center"
                            >
                                <CaretDownSlim class="w-2 h-auto rotate-180"></CaretDownSlim>
                                <span>{{ t('credit-label.button-close') }}</span>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-32 pb-24 items-center w-full">
                <component
                    v-for="component in component_list"
                    :is="component.component"
                    :content="component.content"
                    :properties="component.properties"
                    :items="component.items"
                />
            </div>

            <div v-if="person" class="flex flex-col gap-22 w-full large-container">
                <div class="w-full">
                    <ContactBannerMoreInfo
                        :title="t('contact.title')"
                        :profile-picture="person_image"
                        :text="content?.data?.contactBanner?.body"
                    />
                </div>
            </div>

            <RelatedProjects
                v-if="content.data.relatedProjects"
                :left="content.data.relatedProjects['1']"
                :middle="content.data.relatedProjects['2']"
                :right="content.data.relatedProjects['3']"
            />


        </div>
        <!--        <div class="w-full bg-red-500 h-32 z-30">Footer</div>-->
        <Footer />
    </div>
</template>

<script lang="ts" setup>

import Quote from "~/components/view/Quote.vue"
import BlockText from "~/components/view/BlockText.vue"
import ImageWithTextOverlay from "~/components/view/ImageWithTextOverlay.vue"
import VideoWithText from "~/components/view/VideoWithText.vue"
import { useRoute } from "vue-router"
import CaretDownSlim from "~/components/icons/CaretDownSlim.vue"
import ImageWithTextBlock from "~/components/view/ImageWithTextBlock.vue"
import DuoParalaxImages from "~/components/view/DuoParalaxImages.vue"
import DuoParalaxMedia from "~/components/view/DuoParalaxMedia.vue"
import ImageCarouselRegular from "~/components/view/ImageCarouselRegular.vue"
import MediaCarousel from "~/components/view/MediaCarousel.vue"
import MediaWithTextBlock from "~/components/view/MediaWithTextBlock.vue"
import MediaWithCaption from "~/components/view/MediaWithCaption.vue"
import LargeText from "~/components/view/LargeText.vue"
import BehindTheScenes from "~/components/view/BehindTheScenes.vue"
import ContactBannerMoreInfo from "~/components/view/ContactBannerMoreInfo.vue"
import RelatedProjects from "~/components/view/RelatedProjects.vue"
import VideoWithTextBlock from "~/components/view/VideoWithTextBlock.vue"
import { Pages } from "#shared/types/Pages"
import Footer from "~/components/view/Footer.vue"
import RenderedHTML from "~/components/view/RenderedHTML.vue"
import SetImg from "~/components/view/SetImg.vue"
import YoutubeEmbed from "~/components/UI/YoutubeEmbed.vue"
import type { ApiResponse } from "#layers/cms/shared/types/ApiResponse"
import { kebabCase } from 'lodash-es'
import BlockText2Columns from "~/components/view/BlockText2Columns.vue"

const router = useRoute()
const requestFetch = useRequestFetch()
const { t } = useI18n({ useScope: 'local' })
const mainStore = useMainStore()
const siteStore = useSiteStore()
const config = useRuntimeConfig()
const { get_hyphenated_content } = useTextUtils()
const {get_value: get_localized_value} = useLocalizedValue()


definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: (el) => {
            if (window) window.scroll({top: 0})
        }
    }
})


const component_list: {
    component: typeof Quote | typeof BlockText | typeof DuoParalaxImages | typeof DuoParalaxMedia | typeof YoutubeEmbed | typeof VideoWithTextBlock | typeof BehindTheScenes | typeof LargeText | typeof ImageCarouselRegular | typeof MediaCarousel | typeof ImageWithTextBlock | typeof MediaWithTextBlock | typeof MediaWithCaption,
    content: any,
    properties: object,
    items?: any[]
}[] = []

const show_full_credit = ref(false)
const show_video = ref(true)


const { elementRef: elementRef } = useInViewport({
    threshold: 0.95,
    onEnter: () => {
        siteStore.setIconColor('light')
    },
    onLeave: () => {
        siteStore.setIconColor('dark')
    },
})

const { elementRef: videoTrackingElement } = useInViewport({
    threshold: 0,
    onEnter: () => {
        show_video.value = true
    },
    onLeave: () => {
        show_video.value = false
    },
})

const retrieve_content = async () => {
    const result = await requestFetch<ApiResponse>(`/api/bucketContent/website-cases/${ router.params.slug }?by_slug=true`)
    if (!result.success) throw new Error(result.message)
    return result.data
}

// Fired in parallel with the bucket-schema fetch inside useContentReference below, instead of
// blocking it — the schema doesn't depend on this content, only on the 'website-cases' bucket name.
const content_promise = retrieve_content()

const {
    content: person,
    label: person_name,
    image: person_image
} = await useContentReference({
    sourceBucket: 'website-cases',
    sourceData: content_promise.then(c => c.data),
    fieldPath: 'contactBanner.person'
})

const content = await content_promise

// Credits saved by the CMS editor are keyed in kebab-case (`fit-out`), the schema and translations use snake_case.
const creditKey = (key: string) => key.replace(/-/g, '_')

const credits = computed(() => {
    const contentCredits = content.data.credits ?? {}
    const bucketMeta = mainStore.buckets.find(bucket => bucket.slug === 'website-cases')
    const creditOrder = Object.keys(bucketMeta?.jsonSchema.properties.credits?.properties ?? {})
    const creditOrderByKey = new Map(creditOrder.map((key, index) => [key, index]))

    return Object.keys(contentCredits)
        .filter(credit_title => credit_title !== 'NaN')
        .sort((left, right) => {
            const fallbackOrder = creditOrder.length
            return (creditOrderByKey.get(creditKey(left)) ?? fallbackOrder) - (creditOrderByKey.get(creditKey(right)) ?? fallbackOrder)
        })
        .map(credit_title => {
            if (contentCredits[credit_title]) {
                return {
                    label: credit_title,
                    key: creditKey(credit_title),
                    value: get_localized_value(contentCredits[credit_title]),
                }
            }
        })
        .filter(item => item != undefined)
})

const create_components = () => {
    const keys = Object.keys(content.data.content) as Array<keyof typeof content.data.content>
    keys.forEach(component_key => {
        const component_type = kebabCase(content.data.content[component_key]["component-type"])
        if (component_type === 'quote') {
            component_list.push({
                component: Quote,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'block-text' || component_type === 'blocktext') {
            component_list.push({
                component: BlockText,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'block-text-2-columns') {
            component_list.push({
                component: BlockText2Columns,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'quote-with-image') {
            component_list.push({
                component: Quote,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'image') {
            component_list.push({
                component: ImageWithTextOverlay,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'video-with-text') {
            component_list.push({
                component: VideoWithText,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'image-with-text-block') {
            component_list.push({
                component: ImageWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'duo-paralax-images') {
            component_list.push({
                component: DuoParalaxImages,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'duo-paralax-media') {
            component_list.push({
                component: DuoParalaxMedia,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'image-carousel') {
            component_list.push({
                component: ImageCarouselRegular,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.images
            })
        } else if (component_type === 'media-carousel') {
            component_list.push({
                component: MediaCarousel,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.items
            })
        } else if (component_type === 'media-with-text-block') {
            component_list.push({
                component: MediaWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'media-with-caption') {
            component_list.push({
                component: MediaWithCaption,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'large-text') {
            component_list.push({
                component: LargeText,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.images
            })
        } else if (component_type === 'behind-the-scenes') {
            component_list.push({
                component: BehindTheScenes,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.items
            })
        } else if (component_type === 'video-with-text-block') {
            component_list.push({
                component: VideoWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
            })
        } else if (component_type === 'youtube-video-with-text-block') {
            component_list.push({
                component: YoutubeEmbed,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
            })
        }
    })
}

create_components()

const seo_title = get_hyphenated_content(get_localized_value<string>(content.data.seo?.title) ?? get_localized_value<string>(content?.data.title)) || 'Case'
const seo_description = get_localized_value<string>(content.data.seo?.description) ?? get_localized_value<string>(content?.data?.subtitle) ?? undefined
const seo_image = content.data.seo?.image ?? content.data['hero-image']
const page_url = `${config.public.publicHost}${router.fullPath}`

useHead({
    title: seo_title,
    script: [{
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            headline: seo_title,
            description: seo_description,
            image: seo_image ? `${config.public.publicHost}/api/files/image/${seo_image}` : undefined,
            datePublished: content._versioning.created,
            dateModified: content._versioning.updated,
            mainEntityOfPage: page_url,
            publisher: {
                '@type': 'Organization',
                name: 'YIPP',
                url: config.public.publicHost
            }
        }).replace(/</g, '\\u003c')
    }]
})
useSeoMeta({
    description: seo_description,
    ogTitle: seo_title,
    ogDescription: seo_description,
    ogImage: seo_image ? `${config.public.publicHost}/api/files/image/${seo_image}` : undefined,
    ogUrl: page_url
})

onMounted(() => {
    siteStore.setIconColor('light')
    siteStore.setCurrentPage(Pages.WORK)
})
</script>

<i18n lang="yaml">
en:
    credit-label:
        button-open: Show all credits
        button-close: Close credits
        client_and_location: Client and Location
        open_since: Open Since
        square_meters: Square meters (m2)
        square_meters_m_2: Square meters (m2)
        our_role: Our Role
        exhibition_designer: Exhibition Designer
        fit_out: Fit Out
        av_partners: AV Partners
        av_partner: AV Partners
        photography: Photography
        hardware: Hardware
        other_credits: Other Credits
    placeholder: No image yet
    contact:
        title: Want to know more?
        body: asfasfa
        button: Contact us
nl:
    credit-label:
        button-open: Toon alle credits
        button-close: Sluit credits
        client_and_location: Klant en locatie
        open_since: Open sinds
        square_meters: Vierkante meters (m2)
        square_meters_m_2: Vierkante meters (m2)
        our_role: Onze rol
        exhibition_designer: Exhibition Ontwerper
        fit_out: Fit Out
        av_partners: AV Partners
        av_partner: AV Partners
        photography: Fotografie
        hardware: Hardware
        other_credits: Andere credits
    placeholder: No image yet
    contact:
        title: Meer weten?
        body: Our projectmanager Michelle van der Hoeven would love to tell you about this project!
        button: Contact us
</i18n>
